import { useState, useEffect, useCallback } from "react";
import { getAdminUsers, updateAdminUser, deleteAdminUser } from "../../lib/api";
import { toList } from "../../lib/apiAdminUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Pencil, MoreHorizontal, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const BILLING_ROLES = ["admin", "founder", "free", "paid", "trial"];
const ROLE_LABELS = { admin: "Admin", founder: "Fundador", free: "Gratis", paid: "Pagado", trial: "Prueba" };
const ROLE_COLORS = {
  admin: "bg-violet-100 text-violet-800 border-violet-200",
  founder: "bg-violet-100 text-violet-800 border-violet-200",
  free: "bg-sky-100 text-sky-800 border-sky-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  trial: "bg-amber-100 text-amber-800 border-amber-200",
};
const SUB_STATUSES = ["trial", "active", "past_due", "cancelled", "expired"];
const SUB_LABELS = {
  trial: "Prueba",
  active: "Activo",
  past_due: "Vencido",
  cancelled: "Cancelado",
  expired: "Expirado",
};
const SUB_COLORS = {
  trial: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  past_due: "bg-red-100 text-red-800",
  cancelled: "bg-slate-100 text-slate-800",
  expired: "bg-red-100 text-red-800",
};

function formatDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

function isFounderUser(u) {
  if (u.is_founder === true) return true;
  return String(u.registration_source || "").toLowerCase().includes("fundadores");
}

function isOwnerUser(u) {
  return (u.role || "").toLowerCase() === "owner" || (u.billing_role || "").toLowerCase() === "owner";
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    billing_role: "trial",
    subscription_status: "trial",
    trial_ends_at: "",
    is_active: true,
    is_billing_exempt: false,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers();
      setUsers(toList(res?.data));
    } catch (err) {
      setUsers([]);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      billing_role: u.billing_role || "trial",
      subscription_status: u.subscription_status || "trial",
      trial_ends_at: u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : "",
      is_active: u.is_active !== false,
      is_billing_exempt: (u.billing_role || "").toLowerCase() === "free" || (u.billing_role || "").toLowerCase() === "admin",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      const payload = {
        billing_role: form.billing_role,
        subscription_status: form.subscription_status,
        is_active: form.is_active,
      };
      if (form.trial_ends_at) payload.trial_ends_at = form.trial_ends_at;
      if (form.is_billing_exempt) payload.billing_role = "free";
      await updateAdminUser(editingUser.id, payload);
      toast.success("Usuario actualizado");
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const quickAction = async (userId, action) => {
    try {
      if (action === "admin") await updateAdminUser(userId, { billing_role: "admin" });
      else if (action === "founder") await updateAdminUser(userId, { billing_role: "founder" });
      else if (action === "free") await updateAdminUser(userId, { billing_role: "free" });
      else if (action === "extend_trial") {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        await updateAdminUser(userId, {
          billing_role: "trial",
          subscription_status: "trial",
          trial_ends_at: d.toISOString().slice(0, 10),
        });
      } else if (action === "suspend")
        await updateAdminUser(userId, { is_active: false });
      else if (action === "activate")
        await updateAdminUser(userId, { is_active: true });
      toast.success("Acción aplicada");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openDeleteConfirm = (u) => {
    if (isOwnerUser(u)) return;
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete?.id) return;
    setDeleting(true);
    try {
      await deleteAdminUser(userToDelete.id);
      toast.success("Usuario eliminado");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Usuarios</h1>
        <p className="text-slate-500 mt-1">Gestión de usuarios y roles de facturación</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista de usuarios</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Cargando...</div>
          ) : users.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No hay usuarios</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Fuente</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Suscripción</TableHead>
                  <TableHead>Trial hasta</TableHead>
                  <TableHead>Registro</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className={u.is_active === false ? "opacity-60" : ""}>
                    <TableCell className="font-medium">{u.name || u.nombre || u.email}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.pais || "-"}</TableCell>
                    <TableCell
                      className="text-sm text-slate-600 max-w-[180px] truncate"
                      title={u.registration_source || undefined}
                    >
                      {u.registration_source || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ROLE_COLORS[(u.billing_role || "").toLowerCase()] || ""}>
                        {ROLE_LABELS[(u.billing_role || "").toLowerCase()] || u.billing_role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isFounderUser(u) ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                          🌅 Fundador 3M
                        </Badge>
                      ) : (
                        <Badge variant="outline" className={SUB_COLORS[(u.subscription_status || "").toLowerCase()] || ""}>
                          {SUB_LABELS[(u.subscription_status || "").toLowerCase()] || u.subscription_status || "-"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(u.trial_ends_at)}</TableCell>
                    <TableCell>{formatDate(u.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => quickAction(u.id, "admin")}>
                            Asignar admin
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => quickAction(u.id, "founder")}>
                            Asignar fundador
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => quickAction(u.id, "free")}>
                            Acceso gratuito
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => quickAction(u.id, "extend_trial")}>
                            Extender prueba 30 días
                          </DropdownMenuItem>
                          {u.is_active !== false ? (
                            <DropdownMenuItem onClick={() => quickAction(u.id, "suspend")} className="text-red-600">
                              Suspender
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => quickAction(u.id, "activate")}>
                              Reactivar
                            </DropdownMenuItem>
                          )}
                          {!isOwnerUser(u) && (
                            <DropdownMenuItem
                              onClick={() => openDeleteConfirm(u)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-slate-600">{editingUser.email}</p>
              <div>
                <label className="text-sm font-medium">Rol de facturación</label>
                <Select
                  value={form.billing_role}
                  onValueChange={(v) => setForm({ ...form, billing_role: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Estado de suscripción</label>
                <Select
                  value={form.subscription_status}
                  onValueChange={(v) => setForm({ ...form, subscription_status: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUB_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {SUB_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Trial termina</label>
                <input
                  type="date"
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  value={form.trial_ends_at}
                  onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm">Usuario activo</label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-800 hover:bg-slate-700">
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Estás seguro de que deseas eliminar a{" "}
            <span className="font-semibold text-slate-900">
              {userToDelete?.name || userToDelete?.nombre || userToDelete?.email}
            </span>
            ? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
            >
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
